import { useState } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import {
  useDetectionFeedback,
  type DebateMap,
} from "@/hooks/useFieldDebate";

type Detection = NonNullable<DebateMap["detection"]>;

/** Additive, hedged shadow-detection annotation (spec 2026-07-13).
 * Silent unless the debate frame deserves a hedge: TAXONOMY, or
 * opposition that exists but isn't joined in this corpus. Copy stays
 * hedged ("may", "potential") — never an authoritative reclassification;
 * do not strengthen without a detection-gate run + design review. */
export function DetectionBanner({
  detection,
  conceptId,
}: {
  detection: Detection;
  conceptId: string;
}) {
  const feedback = useDetectionFeedback(conceptId);
  // One-shot local vote (optimistic + rollback) — usePlacementFeedback
  // precedent: feedback given is UI state, not map data.
  const [voted, setVoted] = useState<"thumbs_up" | "thumbs_down" | null>(null);

  const axes = detection.axes ?? [];
  const anyJoined = axes.some((a) => a.joined);

  let message: string | null = null;
  if (anyJoined) {
    // A joined axis dominates EVERY classification (Codex review M1): the
    // model can say TAXONOMY while keeping an axis that population data
    // marks joined — hedging a live debate is the one trust failure this
    // feature must never commit. Silence wins.
    message = null;
  } else if (detection.classification === "TAXONOMY") {
    message =
      "These camps may be compatible research approaches rather than " +
      "opposing positions — treat the boundaries as themes, not sides.";
  } else if (axes.length > 0) {
    // The axis closest to joined: max over axes of min(pole populations).
    const nearest = axes.reduce((best, a) =>
      Math.min(a.pole_a_papers, a.pole_b_papers) >
      Math.min(best.pole_a_papers, best.pole_b_papers)
        ? a
        : best,
    );
    const [hi, lo] =
      nearest.pole_a_papers >= nearest.pole_b_papers
        ? [nearest.pole_a_papers, nearest.pole_b_papers]
        : [nearest.pole_b_papers, nearest.pole_a_papers];
    message =
      `There's a potential debate here — ${nearest.description} — but one ` +
      `side is thin in your corpus (${hi} vs ${lo} papers), so the ` +
      "disagreement isn't joined yet.";
  }
  if (!message) return null;

  const vote = (signal: "thumbs_up" | "thumbs_down") => {
    setVoted(signal);
    // detected_at pins the vote to the record being judged — the server
    // 409s if a rebuild landed a newer detection first (Codex review M2).
    feedback.mutate(
      { signal, detected_at: detection.detected_at },
      { onError: () => setVoted(null) },
    );
  };

  return (
    <div className="mb-2 flex items-center gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
      <span className="flex-1">{message}</span>
      {voted ? (
        <span className="shrink-0 text-muted-foreground">Thanks</span>
      ) : (
        <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
          <span className="hidden sm:inline">Match your read of the field?</span>
          <button
            type="button"
            aria-label="Yes, this matches"
            className="rounded p-1 hover:bg-amber-500/20 hover:text-foreground"
            onClick={() => vote("thumbs_up")}
          >
            <ThumbsUp className="h-3 w-3" />
          </button>
          <button
            type="button"
            aria-label="No, this is off"
            className="rounded p-1 hover:bg-amber-500/20 hover:text-foreground"
            onClick={() => vote("thumbs_down")}
          >
            <ThumbsDown className="h-3 w-3" />
          </button>
        </span>
      )}
    </div>
  );
}
